import React, { useContext, useEffect, useRef, useState } from "react";
import { IfcViewerAPI } from "web-ifc-viewer";
import * as THREE from "three";
import { GetRegionOid } from "../API/region.api";
import { createNewBasket } from "../API/basket.api";
import { useParams } from "react-router-dom";
import { CultureContext, ICultureContextType } from "../Context/cultureContext";
import { MyConfig } from "../API/myConfig";
import { IconButton, Tooltip } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import topView from "../Components/Images/top View.png";
import leftView from "../Components/Images/Left View.png";
import rightView from "../Components/Images/Right View.png";
import bottomView from "../Components/Images/Bottom View.png";
import frontView from "../Components/Images/Front View.png";
import backView from "../Components/Images/Back View.png";
import CircularProgress from "@mui/material/CircularProgress";
import "./pages.scss";

// interface IRegionType {
//   label: string;
//   oid: string;
//   productCount: number;
//   translation: string;
// }
interface IfcViewerProps {
  productDetails: any;
}

const IfcViewer: React.FC<IfcViewerProps> = ({ productDetails }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<IfcViewerAPI | null>(null);
  const modelRef = useRef<THREE.Object3D | null>(null);
  const boxRef = useRef<THREE.Box3 | null>(null);
  const centerRef = useRef<THREE.Vector3 | null>(null);
  const { region } = useParams();
  const { regions } = useContext(CultureContext) as ICultureContextType;
  const [ifcFileURL, setIfcFileURL] = useState("");
  const [loading, setLoading] = useState(true);

  const waitForIFC = async (viewer: IfcViewerAPI) => {
    return new Promise<IfcViewerAPI>((resolve) => {
      const interval = setInterval(() => {
        if (viewer.IFC) {
          clearInterval(interval);
          resolve(viewer);
        }
      }, 1000);
    });
  };

  const ifcFiles = productDetails.fileDescriptors.filter((file: any) =>
    file.fileName?.toLowerCase().endsWith(".ifc")
  );


  useEffect(() => {
    if (!containerRef.current) return;
    const viewer = new IfcViewerAPI({ container: containerRef.current });
    viewerRef.current = viewer;
    viewer.context.getScene().background = new THREE.Color("#ffffff");

    const loadModel = async () => {
      let config = await MyConfig();
      await waitForIFC(viewer);
      viewer.IFC.setWasmPath("../../wasm/");

      try {
        const model = await viewer.IFC.loadIfcUrl(
          `${config.REACT_APP_API_URL_UAT}/anonymous/file/${ifcFiles[0].fileId}`
        );

        modelRef.current = model;
        //model.scale.set(2, 2, 2);
        // compute bounding box + center
        model.traverse((child: any) => {
          if (child instanceof THREE.Mesh) {
            child.geometry.computeBoundingBox();
            child.geometry.computeBoundingSphere();
          }
        });

        const box = new THREE.Box3().setFromObject(model);
        boxRef.current = box;

        const center = new THREE.Vector3();
        box.getCenter(center);
        centerRef.current = center;
        // center model
        model.position.sub(center);
        // model.position.y -= box.min.y;
        model.position.y -= 0.25;

        const helper = new THREE.Box3Helper(box);
        viewer.context.scene.add(helper);
        viewer.context.fitToFrame();
      } catch (err) {
        console.error("Error loading IFC model:", err);
      } finally {
        setLoading(false);
      }
    };

    loadModel();

    return () => {
      viewer.dispose();
    };
  }, []);

  //#region button click

  const handleTopView = async () => {
    const viewer = viewerRef.current;
    const model = modelRef.current;

    if (!viewer || !model) {
      console.error("Viewer or model not ready");
      return;
    }

    // Scale the model
    //model.scale.set(2, 2, 2);

    // Optionally scale meshes inside
    model.traverse((child: any) => {
      if (child.isMesh) {
        child.scale.set(2, 2, 2);
      }
    });

    // Compute bounds for camera positioning
    const box = new THREE.Box3().setFromObject(model);
    const size = new THREE.Vector3();
    box.getSize(size);
    const center = new THREE.Vector3();
    box.getCenter(center);

    const cameraHeight = size.length() * 2;

    // Switch to orthographic
    viewer.context.ifcCamera.toggleProjection();

    // Move camera above model
    const controls = viewer.context.getIfcCamera().cameraControls;
    await controls.setLookAt(
      center.x,
      center.y + cameraHeight,
      center.z,
      center.x,
      center.y,
      center.z,
      true
    );
  };

  const handleBottomView = async () => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const cameraControls = viewer.context.getIfcCamera().cameraControls;
    if (!cameraControls || !cameraControls.setLookAt) {
      console.error("CameraControls or setLookAt not found");
      return;
    }

    if (!modelRef.current) {
      console.error("No IFC model loaded");
      return;
    }
    // Scale the model
    modelRef.current.scale.set(2, 2, 2);
    const box = new THREE.Box3().setFromObject(modelRef.current);
    const size = new THREE.Vector3();
    box.getSize(size);
    const cameraHeight = size.length() * 2;

    viewer.context.ifcCamera.toggleProjection();

    await cameraControls.setLookAt(0, -cameraHeight, 0, 0, 0, 0, true);

    // viewer.context.fitToFrame();
  };


  const handleRightView = async () => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const cameraControls = viewer.context.getIfcCamera().cameraControls;
    if (!cameraControls || !cameraControls.setLookAt) {
      console.error("CameraControls or setLookAt not found");
      return;
    }

    if (!modelRef.current) {
      console.error("No IFC model loaded");
      return;
    }

    modelRef.current.scale.set(2, 2, 2);
    const box = new THREE.Box3().setFromObject(modelRef.current);
    const size = new THREE.Vector3();
    box.getSize(size);
    const cameraDistance = size.length() * 2;

    viewer.context.ifcCamera.toggleProjection();

    await cameraControls.setLookAt(
      cameraDistance,
      0,
      0, // right side
      0,
      0,
      0, // look at model center
      true
    );

    // viewer.context.fitToFrame();
  };

  const handleLeftView = async () => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const cameraControls = viewer.context.getIfcCamera().cameraControls;
    if (!cameraControls || !cameraControls.setLookAt) {
      console.error("CameraControls or setLookAt not found");
      return;
    }

    if (!modelRef.current) {
      console.error("No IFC model loaded");
      return;
    }

    modelRef.current.scale.set(2, 2, 2);
    const box = new THREE.Box3().setFromObject(modelRef.current);
    const size = new THREE.Vector3();
    box.getSize(size);
    const cameraDistance = size.length() * 2;

    viewer.context.ifcCamera.toggleProjection();

    await cameraControls.setLookAt(
      -cameraDistance,
      0,
      0, // left side
      0,
      0,
      0, // look at model center
      true
    );
  };
  //#endregion button click

  return (
    <>
      <div
        ref={containerRef}
        className="responsive-container"
      >
        {loading ? (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(255,255,255,0.7)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10,
            }}
          >
            {/* <Loader /> */}
            <CircularProgress color="primary" />
          </div>
        ) :
          (
            < div
              className="responsive-sideControlPanel"
            >
              <Tooltip title="Top View">
                <IconButton size="medium" color="primary" onClick={handleTopView}>
                  {/* <ArrowDownwardIcon /> */}
                  <img
                    src={topView}
                    className="img-responsive"
                    id="topView"
                    alt={"Top View"}
                    loading="lazy"
                    height="30"
                  />
                </IconButton>
              </Tooltip>

              <Tooltip title="Bottom View">
                <IconButton size="medium" color="primary" onClick={handleBottomView}>
                  {/* <ArrowUpwardIcon /> */}
                  <img
                    src={bottomView}
                    className="img-responsive"
                    id="bottomView"
                    alt={"Bottom View"}
                    loading="lazy"
                    height="30"
                  />
                </IconButton>
              </Tooltip>

              <Tooltip title="Left View">
                <IconButton size="medium" color="primary" onClick={handleLeftView}>
                  {/* <ArrowBackIcon /> */}
                  <img
                    src={leftView}
                    className="img-responsive"
                    id="leftView"
                    alt={"Left View"}
                    loading="lazy"
                    height="30"
                  />
                </IconButton>
              </Tooltip>

              <Tooltip title="Right View">
                <IconButton size="medium" color="primary" onClick={handleRightView}>
                  {/* <ArrowForwardIcon /> */}
                  <img
                    src={rightView}
                    className="img-responsive"
                    id="rightView"
                    alt={"Right View"}
                    loading="lazy"
                    height="30"
                  />
                </IconButton>
              </Tooltip>
              {/* <Tooltip title="Front View">
            <IconButton size="small" color="primary" onClick={handleLeftView}>
              <img
                src={frontView}
                className="img-responsive"
                id="frontView"
                alt={"Front View"}
                loading="lazy"
              />
            </IconButton>
          </Tooltip>

          <Tooltip title="Back View">
            <IconButton size="small" color="primary" onClick={handleLeftView}>
              <img
                src={backView}
                className="img-responsive"
                id="backView"
                alt={"Back View"}
                loading="lazy"
              />
            </IconButton>
          </Tooltip> */}
            </div>)}

      </div>

      <div
        style={{
          backgroundColor: "#1976d2",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          // padding: "10px",
          flexWrap: "wrap",
          marginBottom: "10px"
        }}
      >
        <img
          src="/3dModel.jpg"
          alt="IFC Viewer Logo"
          height={35}
          style={{ marginRight: 8 }}
        />
        <span style={{ color: "#fff", fontWeight: "bold", fontSize: 14 }}>3D View</span>
      </div>
    </>
  );
};

export default IfcViewer;